// realtime/echo.js
//
// Laravel Echo (Reverb) bootstrap for the PWA — FOUNDATION ONLY.
//
// This wires the websocket transport and connection lifecycle. It does NOT
// subscribe to any channel; feature screens do that later via @laravel/echo-vue
// composables (useEcho / useEchoNotification / …), which auto-leave on unmount.
//
// ── Why a custom authorizer ───────────────────────────────────────────────────
// The PWA authenticates via Sanctum STATEFUL session cookies, cross-subdomain
// (app.poolit.* → poolit.*). Private-channel auth is a POST to the Laravel app's
// /broadcasting/auth (NOT to Reverb). echo-vue's default authorizer would send a
// bare request with no cookie / XSRF header and 401/419. So we route the auth POST
// through the app's existing axios instance (apiClient), inheriting for free:
//   • withCredentials + the XSRF-TOKEN → X-XSRF-TOKEN header (Sanctum),
//   • the 419/CSRF re-prime-and-retry interceptor,
//   • the 401 → auth:unauthorized → logout handling.
//
// ── Why the baseURL override ──────────────────────────────────────────────────
// apiClient's baseURL is `${VITE_API_BASE_URL}/api/v1`, but /broadcasting/auth is
// mounted at the web ROOT. We mirror the exact pattern initializeCsrfToken() uses
// for /sanctum/csrf-cookie: a root-relative URL + an explicit
// `baseURL: VITE_API_BASE_URL || ''` per request. This resolves to
// https://poolit.test/broadcasting/auth in dev/prod, and to a same-origin
// /broadcasting/auth in tunnel mode — never the wrong /api/v1/broadcasting/auth.
//
// ── Env → transport ───────────────────────────────────────────────────────────
// configureEcho() auto-fills key / wsHost / wsPort / wssPort / forceTLS /
// enabledTransports from the VITE_REVERB_* vars (see src/.env*). Those bake in at
// build time — a change needs `npm run build`.
import { configureEcho, echo, echoIsConfigured } from '@laravel/echo-vue';
import { apiClient } from '@/httpConfig/config/axios.js';
import { REALTIME_STATUS, useRealtimeStore } from '@/stores/realtimeStore.js';

// Run-once guards + the visibility handle so init is idempotent and teardown can
// detach cleanly (see the initRealtime() call site in App.vue).
let started = false;
let visibilityHandler = null;

// Callbacks fired on every RE-connect (a 'connected' transition AFTER the first).
// Feature screens register a refetch here to fill the gap left by events that
// fired while the socket was down — websockets have no replay buffer.
const reconnectListeners = new Set();

/**
 * Map pusher-js's six raw connection states to the five UI-facing states.
 *
 * Crucially, 'unavailable' (a TRANSIENT drop that pusher-js auto-retries roughly
 * every 15s) maps to 'reconnecting', NOT 'failed'. 'failed' is reserved for the
 * terminal "websockets unsupported / gave up" state that does not auto-retry.
 * @laravel/echo-vue's built-in connectionStatus() conflates the two into
 * 'failed'; we keep them distinct so a UI indicator can show "reconnecting…".
 *
 * @param {string} raw pusher-js connection.state
 * @returns {string} one of REALTIME_STATUS.*
 */
export function mapConnectionState(raw) {
  switch (raw) {
    case 'connected':
      return REALTIME_STATUS.CONNECTED;
    case 'connecting':
    case 'initialized':
      return REALTIME_STATUS.CONNECTING;
    case 'unavailable':
      return REALTIME_STATUS.RECONNECTING;
    case 'failed':
      return REALTIME_STATUS.FAILED;
    case 'disconnected':
    default:
      return REALTIME_STATUS.DISCONNECTED;
  }
}

/**
 * The custom Sanctum-cookie authorizer. Reused for private AND presence channels.
 * @param {{ name: string }} channel
 */
function broadcastingAuthorizer(channel) {
  return {
    authorize(socketId, callback) {
      apiClient
        .post(
          '/broadcasting/auth',
          { socket_id: socketId, channel_name: channel.name },
          // Root-relative URL + baseURL override (see file header) so the POST
          // hits /broadcasting/auth at the web root, not under /api/v1.
          { baseURL: import.meta.env.VITE_API_BASE_URL || '' },
        )
        .then((response) => callback(false, response.data))
        .catch((error) => callback(true, error));
    },
  };
}

// The Echo configuration reused by BOTH the initial configure and the teardown
// reset. broadcaster + custom authorizer only; key / wsHost / wsPort / wssPort /
// forceTLS / enabledTransports are auto-filled from VITE_REVERB_* (see header) —
// override here only if that ever changes.
const REALTIME_CONFIG = {
  broadcaster: 'reverb',
  authorizer: broadcastingAuthorizer,
};

/**
 * Register the Echo configuration (broadcaster + custom authorizer) exactly once.
 * Does NOT open a connection — that happens on the first echo() call.
 */
export function configureRealtime() {
  if (echoIsConfigured()) return;
  configureEcho(REALTIME_CONFIG);
}

/**
 * Subscribe to reconnect events. The callback fires on each transition back to
 * 'connected' AFTER the initial connect (i.e. recovery from a drop), so feature
 * screens can refetch authoritative state to fill the gap.
 *
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
export function onReconnected(callback) {
  reconnectListeners.add(callback);
  return () => reconnectListeners.delete(callback);
}

/**
 * Whether realtime is currently LIVE — initRealtime() has run and
 * teardownRealtime() has not since discarded the socket.
 *
 * Deliberately DISTINCT from echoIsConfigured(): teardownRealtime() re-runs
 * configureEcho() (so echoIsConfigured() stays true even after a teardown), but
 * leaves `started` false. A caller that must NOT resurrect a torn-down socket —
 * e.g. a feature composable leaving its channel on unmount during logout, where
 * a bare echo() call would rebuild an auto-connecting Pusher and re-auth the
 * departing user's private channel — gates on THIS, not on "configured".
 *
 * @returns {boolean}
 */
export function isRealtimeStarted() {
  return started;
}

/**
 * Whether the socket is actually CONNECTED right now (not merely started). True
 * only when initRealtime() has run AND the pusher-js connection is in the
 * 'connected' state — false in tunnel/offline mode where the socket started but
 * never reached a server, and false after teardown.
 *
 * A feature that patches state both optimistically (from an HTTP response) AND
 * from an authoritative broadcast uses this to avoid double-applying: when the
 * socket is connected the broadcast will arrive and own that state, so the
 * optimistic patch can defer to it; when it's NOT connected (no broadcast is
 * coming) the optimistic patch must carry the update itself. The `started` guard
 * ensures this never reconstructs a torn-down socket.
 *
 * @returns {boolean}
 */
export function isRealtimeConnected() {
  if (!started) return false;
  try {
    return echo()?.connector?.pusher?.connection?.state === 'connected';
  } catch {
    return false;
  }
}

/**
 * Bootstrap realtime: configure Echo, open the socket, and wire the connection
 * lifecycle into the realtime store + reconnect/visibility hooks. Idempotent.
 *
 * Call ONCE, after auth is known and only for authenticated users (every Poolit
 * channel is a private Sanctum channel). Does NOT subscribe to any channel.
 *
 * @returns {object|null} the Echo singleton (or null if unavailable)
 */
export function initRealtime() {
  if (started) return echo();

  configureRealtime();

  // The first echo() call instantiates the singleton and opens the pusher-js
  // socket (no channels subscribed → an idle authenticated connection).
  const instance = echo();
  const connection = instance?.connector?.pusher?.connection;
  if (!connection) {
    // Non-pusher transport or connector not ready — nothing to bind. Leave
    // `started` false so a later call can retry.
    return instance;
  }

  const store = useRealtimeStore();

  // Seed with the current state (pusher may already be 'connecting').
  store.setStatus(mapConnectionState(connection.state));

  let hasConnectedOnce = false;

  connection.bind('state_change', ({ current }) => {
    store.setStatus(mapConnectionState(current));
  });

  connection.bind('connected', () => {
    store.markConnected();
    if (hasConnectedOnce) {
      // Recovery from a drop — let feature screens refetch the gap.
      reconnectListeners.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error('[realtime] onReconnected callback threw:', error);
        }
      });
    }
    hasConnectedOnce = true;
  });

  connection.bind('error', (error) => {
    console.error('[realtime] connection error:', error);
  });

  // ── Foreground resume (PWA/mobile) ──────────────────────────────────────────
  // WebSockets live in the page context, not the service worker; the OS kills
  // the socket when the tab is fully backgrounded. On resume, nudge pusher-js to
  // reconnect if it isn't connected. Feature screens additionally refetch via
  // onReconnected once the socket transitions back to 'connected'.
  visibilityHandler = () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    const conn = echo()?.connector?.pusher?.connection;
    if (conn && conn.state !== 'connected') {
      echo().connector.pusher.connect();
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  started = true;
  return instance;
}

/**
 * Tear down realtime — for a hard identity change (logout / act-as swap) so
 * every channel re-auths cleanly under the new identity on the next login.
 *
 * Wired into the auth lifecycle at three call sites (the identity-change
 * vectors that must not leave a socket authenticated as the departing user):
 *   • useAuth.logout()            — explicit sign-out (re-init on next login).
 *   • App.vue auth:unauthorized   — involuntary 401 sign-out (bypasses logout).
 *   • useActAs.purgeIdentityStorage() — act-as enter/exit/switch, before reload.
 * Re-init runs via initRealtime() (App.vue boot for reload paths;
 * useAuth setSessionAndSyncPush for in-place re-login). Safe to call before
 * init (no-ops) and after (idempotent) — `started` is reset here.
 */
export function teardownRealtime() {
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
  }
  visibilityHandler = null;
  reconnectListeners.clear();

  // Only touch the Echo instance if it was ever configured. A teardown on an
  // unauthenticated boot (e.g. a 401 from cookie-bootstrap before initRealtime
  // ran) must be a TRUE no-op — echo-vue's echo() throws "Echo has not been
  // configured" when called unconfigured, which would otherwise log a scary
  // (but harmless) error on a completely normal logged-out boot.
  if (echoIsConfigured()) {
    try {
      // Close the live socket, THEN discard echo-vue's cached singleton so the
      // next initRealtime() builds a FRESH, auto-connecting instance with clean
      // bindings. echo-vue only nulls its cached instance inside configureEcho()
      // (dist: `l && (l = null)`); a plain disconnect() leaves that now-dead
      // instance cached, and initRealtime() — which relies on echo() constructing
      // a new auto-connecting Pusher and never calls connect() itself — would then
      // neither revive the socket nor avoid double-binding connection handlers on
      // the stale connection. Re-configuring with the same config is the
      // sanctioned reset. This is what makes an in-place re-login after
      // logout/401 (no page reload) actually restore realtime.
      echo()?.disconnect?.();
      configureEcho(REALTIME_CONFIG);
    } catch (error) {
      console.error('[realtime] teardown failed:', error);
    }
  }

  started = false;
  try {
    useRealtimeStore().setStatus(REALTIME_STATUS.DISCONNECTED);
  } catch {
    /* pinia not active (teardown before init) — nothing to reset */
  }
}
